import React, { useState, useEffect } from 'react';
import { 
  FileText, Search, Download, Eye, Calendar, DollarSign, 
  Building2, Mail, Printer, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const { api } = useAdminAuth();

  useEffect(() => {
    fetchInvoices();
  }, [search, statusFilter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/api/admin/members/invoices?${params}`);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceDetail = async (invoiceId) => {
    try {
      const response = await api.get(`/api/admin/members/invoices/${invoiceId}`);
      setSelectedInvoice(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const generatePDF = (invoice) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    let y = margin;

    // Header
    pdf.setFillColor(30, 58, 138);
    pdf.rect(0, 0, pageWidth, 40, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('INVOICE', margin, 25);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(invoice.invoice_id, pageWidth - margin, 25, { align: 'right' });

    y = 55;

    // Company Info (Issuer)
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('One Globe Alliance', margin, y);
    y += 5;
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const issuer = invoice.issuer || {};
    pdf.text(issuer.address || '123 Global Trade Center', margin, y);
    y += 4;
    pdf.text(`${issuer.city || 'Singapore'}, ${issuer.country || 'Singapore'}`, margin, y);
    y += 4;
    pdf.text(`Tax ID: ${issuer.tax_id || 'SG-OGA-2024'}`, margin, y);
    y += 4;
    pdf.text(`Email: ${issuer.email || 'billing@oneglobealliance.com'}`, margin, y);

    // Bill To
    y = 55;
    const billToX = pageWidth / 2 + 10;
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BILL TO:', billToX, y);
    y += 6;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(invoice.company_name || 'N/A', billToX, y);
    y += 5;
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(invoice.company_address || '', billToX, y);
    y += 4;
    pdf.text(invoice.country || '', billToX, y);
    y += 4;
    pdf.text(invoice.email || '', billToX, y);

    // Invoice Details Box
    y = 95;
    pdf.setFillColor(245, 247, 250);
    pdf.rect(margin, y, pageWidth - 2 * margin, 20, 'F');
    
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(9);
    y += 7;
    
    const col1 = margin + 5;
    const col2 = margin + 50;
    const col3 = pageWidth / 2 + 5;
    const col4 = pageWidth / 2 + 50;
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('Invoice Date:', col1, y);
    pdf.text('Due Date:', col2, y);
    pdf.text('Member ID:', col3, y);
    pdf.text('Status:', col4, y);
    
    y += 6;
    pdf.setFont('helvetica', 'normal');
    pdf.text(invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A', col1, y);
    pdf.text(invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A', col2, y);
    pdf.text(invoice.member_id || 'N/A', col3, y);
    pdf.text(invoice.status?.toUpperCase() || 'N/A', col4, y);

    // Line Items Table
    y = 130;
    
    // Table Header
    pdf.setFillColor(59, 130, 246);
    pdf.rect(margin, y, pageWidth - 2 * margin, 10, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    y += 7;
    pdf.text('Description', margin + 5, y);
    pdf.text('Qty', pageWidth - margin - 60, y);
    pdf.text('Unit Price', pageWidth - margin - 40, y);
    pdf.text('Total', pageWidth - margin - 15, y, { align: 'right' });

    // Table Rows
    y += 8;
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    
    const items = invoice.items || [{ description: invoice.description, quantity: 1, unit_price: invoice.amount, total: invoice.amount }];
    
    items.forEach((item, index) => {
      if (index % 2 === 0) {
        pdf.setFillColor(249, 250, 251);
        pdf.rect(margin, y - 5, pageWidth - 2 * margin, 10, 'F');
      }
      
      pdf.text(item.description || 'N/A', margin + 5, y);
      pdf.text(String(item.quantity || 1), pageWidth - margin - 60, y);
      pdf.text(`$${(item.unit_price || 0).toFixed(2)}`, pageWidth - margin - 40, y);
      pdf.text(`$${(item.total || 0).toFixed(2)}`, pageWidth - margin - 5, y, { align: 'right' });
      y += 10;
    });

    // Totals
    y += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(pageWidth / 2 + 20, y, pageWidth - margin, y);
    y += 8;
    
    const totalsX = pageWidth / 2 + 25;
    const totalsValueX = pageWidth - margin - 5;
    
    pdf.setFontSize(9);
    pdf.text('Subtotal:', totalsX, y);
    pdf.text(`$${(invoice.subtotal || invoice.amount || 0).toFixed(2)}`, totalsValueX, y, { align: 'right' });
    
    y += 6;
    pdf.text(`Tax (${invoice.tax_rate || 0}%):`, totalsX, y);
    pdf.text(`$${(invoice.tax_amount || 0).toFixed(2)}`, totalsValueX, y, { align: 'right' });
    
    y += 8;
    pdf.setFillColor(30, 58, 138);
    pdf.rect(totalsX - 5, y - 5, pageWidth - margin - totalsX + 10, 12, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Total:', totalsX, y + 3);
    pdf.text(`$${(invoice.total || invoice.amount || 0).toFixed(2)}`, totalsValueX, y + 3, { align: 'right' });

    // Payment Status
    y += 25;
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(10);
    
    if (invoice.status === 'paid') {
      pdf.setFillColor(220, 252, 231);
      pdf.setDrawColor(34, 197, 94);
      pdf.roundedRect(margin, y, 50, 15, 3, 3, 'FD');
      pdf.setTextColor(22, 163, 74);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PAID', margin + 18, y + 10);
      
      if (invoice.paid_date) {
        pdf.setTextColor(100, 100, 100);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Paid on: ${new Date(invoice.paid_date).toLocaleDateString()}`, margin + 55, y + 10);
      }
    }

    // Footer
    const footerY = pdf.internal.pageSize.getHeight() - 20;
    pdf.setTextColor(150, 150, 150);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Thank you for your business!', margin, footerY);
    pdf.text('© 2026 One Globe Alliance. All rights reserved.', pageWidth / 2, footerY, { align: 'center' });
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin, footerY, { align: 'right' });

    // Save
    pdf.save(`Invoice_${invoice.invoice_id}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { bg: 'bg-green-100', text: 'text-green-700' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      overdue: { bg: 'bg-red-100', text: 'text-red-700' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700' }
    };
    return badges[status] || badges.pending;
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      inv.invoice_id?.toLowerCase().includes(searchLower) ||
      inv.member_id?.toLowerCase().includes(searchLower) ||
      inv.company_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
        <p className="text-gray-500">View and download invoice documents</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Invoices</p>
                <p className="text-2xl font-bold">{invoices.length}</p>
              </div>
              <FileText size={24} className="text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Paid</p>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'paid').length}</p>
              </div>
              <CheckCircle size={24} className="text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-bold">
                  {invoices.filter(i => {
                    const date = new Date(i.created_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <Calendar size={24} className="text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">
                  ${invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0).toLocaleString()}
                </p>
              </div>
              <DollarSign size={24} className="text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by invoice ID, member ID, or company..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => {
                  const statusBadge = getStatusBadge(invoice.status);
                  return (
                    <tr key={invoice.invoice_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm">{invoice.invoice_id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm">{invoice.member_id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{invoice.company_name}</p>
                        <p className="text-xs text-gray-500">{invoice.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold">${invoice.total?.toLocaleString()}</span>
                        <span className="text-xs text-gray-500 ml-1">{invoice.currency || 'USD'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${statusBadge.bg} ${statusBadge.text}`}>
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fetchInvoiceDetail(invoice.invoice_id)}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => generatePDF(invoice)}
                          >
                            <Download size={14} className="mr-1" />
                            PDF
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Invoice Details</span>
              {selectedInvoice && (
                <Button size="sm" onClick={() => generatePDF(selectedInvoice)}>
                  <Download size={14} className="mr-1" />
                  Download PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-blue-900">INVOICE</h3>
                  <p className="font-mono text-gray-500">{selectedInvoice.invoice_id}</p>
                </div>
                <Badge className={`${getStatusBadge(selectedInvoice.status).bg} ${getStatusBadge(selectedInvoice.status).text} text-lg px-4 py-1`}>
                  {selectedInvoice.status?.toUpperCase()}
                </Badge>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-500">Bill To</Label>
                  <p className="font-medium">{selectedInvoice.company_name}</p>
                  <p className="text-sm text-gray-600">{selectedInvoice.company_address}</p>
                  <p className="text-sm text-gray-600">{selectedInvoice.country}</p>
                  <p className="text-sm text-gray-600">{selectedInvoice.email}</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-gray-500">Invoice Date:</Label>
                    <span>{selectedInvoice.invoice_date ? new Date(selectedInvoice.invoice_date).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <Label className="text-gray-500">Due Date:</Label>
                    <span>{selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <Label className="text-gray-500">Member ID:</Label>
                    <span className="font-mono">{selectedInvoice.member_id}</span>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-blue-600 text-white">
                    <tr>
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-right">Qty</th>
                      <th className="px-4 py-2 text-right">Unit Price</th>
                      <th className="px-4 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedInvoice.items || [{ description: selectedInvoice.description, quantity: 1, unit_price: selectedInvoice.amount, total: selectedInvoice.amount }]).map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="px-4 py-2">{item.description}</td>
                        <td className="px-4 py-2 text-right">{item.quantity}</td>
                        <td className="px-4 py-2 text-right">${item.unit_price?.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">${item.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal:</span>
                    <span>${(selectedInvoice.subtotal || selectedInvoice.amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tax ({selectedInvoice.tax_rate || 0}%):</span>
                    <span>${(selectedInvoice.tax_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total:</span>
                    <span>${(selectedInvoice.total || selectedInvoice.amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInvoices;
